import { Maximize02, Minimize02, X } from '@untitledui/icons'
import { twJoin } from 'tailwind-merge'

import Button from '@components/designSystem/Button'
import { HorizontalScroller } from '@components/designSystem/HorizontalScroller'
import ShelfLegend from '@components/shelfDetails/ShelfLegend'
import { ALL_VIEW_TYPES, type ViewType } from '@components/shelfDetails/utils'
import useHasSystemRole from '@hooks/useHasSystemRole.ts'
import { useIsWideLayout } from '@hooks/useMediaQuery'
import useTypedTranslation from '@hooks/useTypedTranslation.ts'

import type { useShelf } from '@state/shelf/queries'
import type { PropsWithChildren, ReactNode } from 'react'

interface ShelfWrapperProps extends PropsWithChildren {
    titleId: string // used for accessibility purposes
    shelfName: string
    type: ViewType
    onViewTypeChange: (type: ViewType) => void
    isExpanded: boolean
    onExpandCollapse: () => void // Trigger to switch modes
    onClose?: () => void // Only relevant for modal
    sourceLine?: ReactNode // Only relevant for the day view
}

function ShelfWrapper({ children, titleId, shelfName, type, onViewTypeChange, onClose, isExpanded, onExpandCollapse, sourceLine }: ShelfWrapperProps) {
  const { t } = useTypedTranslation()

  const isWideLayout = useIsWideLayout()
  const isDeveloper = useHasSystemRole('DEVELOPER')

  const typesToRender = isDeveloper ? ALL_VIEW_TYPES : ALL_VIEW_TYPES.filter((viewType) => viewType !== 'image')

  return (
    <div className='flex h-full flex-col justify-between gap-4 pt-7 pb-5'>
      <div className='flex items-center justify-between px-6'>
        <p id={titleId} className='font-medium'>{t('storeMap.shelf.title', { name: shelfName })}</p>
        <Button
          variant='outline'
          size='md'
          iconLeading={X}
          onClick={onClose}
          aria-label="close-shelf-card"
        />
      </div>

      <HorizontalScroller className='px-6'>
        <div className='flex gap-2'>
          {typesToRender.map((viewType) => {
            return (
              <Button key={viewType} size='sm' variant={type === viewType ? 'outline' : 'gray'} onClick={() => onViewTypeChange(viewType)}>
                {t(`storeMap.shelf.${viewType}`)}
              </Button>
            )
          })}
        </div>
      </HorizontalScroller>

      <div className="relative flex-1">
        <div className={twJoin('size-full', type === 'product' && 'bg-tertiary')}>
          {children}
        </div>

        {isWideLayout && (
          <Button
            aria-label={isExpanded ? t('storeMap.shelf.collapseBtn') : t('storeMap.shelf.expandBtn')}
            size='xl'
            variant='outline'
            iconLeading={isExpanded ? Minimize02 : Maximize02}
            className="absolute right-4 -bottom-6 z-20"
            onClick={onExpandCollapse}
          />
        )}
      </div>

      <ShelfLegend showFacingStates={type === 'product'} />

      {sourceLine}
    </div>
  )
}

function ShelfVisualizerSkeleton() {
  return (
    <div className='flex h-full animate-pulse flex-col justify-between gap-4 overflow-hidden pt-7 pb-5'>
      {/* Header skeleton */}
      <div className='flex items-center justify-between px-6'>
        <div className='h-6 w-24 rounded-sm bg-gray-200'></div>
        <div className='size-10 rounded-md bg-gray-200'></div>
      </div>

      {/* View type buttons skeleton */}
      <div className='px-6'>
        <div className='flex gap-2'>
          <div className='h-8 w-24 rounded-md bg-gray-200'></div>
          <div className='h-8 w-24 rounded-md bg-gray-200'></div>
          <div className='h-8 w-20 rounded-md bg-gray-200'></div>
        </div>
      </div>

      {/* Main content skeleton */}
      <div className='relative flex-1 bg-tertiary px-6'>
        <div className='size-full rounded-lg bg-gray-200'></div>
      </div>

      {/* Legend skeleton */}
      <div className='flex gap-5 px-6'>
        <div className='flex items-center gap-2'>
          <div className='size-3 rounded-full bg-gray-300'/>
          <div className='h-4 w-20 rounded-sm bg-gray-200'></div>
        </div>
        <div className='flex items-center gap-2'>
          <div className='size-3 rounded-full bg-gray-300'/>
          <div className='h-4 w-24 rounded-sm bg-gray-200'></div>
        </div>
      </div>
    </div>
  )
}

function ErrorMsg({ error, onClose }: {error: NonNullable<ReturnType<typeof useShelf>['error']>; onClose: () => void}) {
  const { t } = useTypedTranslation()

  let errorMsg = {
    title: t('common.unexpectedError.title'),
    description: t('common.unexpectedError.description'),
  }
  if (error.status === 404) {
    errorMsg = {
      title: t('storeMap.shelf.errors.notFound.title'),
      description: t('storeMap.shelf.errors.notFound.description'),
    }
  }

  return (
    <div className="flex size-full flex-col items-center justify-center gap-3 p-4">
      <h3 className='font-medium'>{errorMsg.title}</h3>
      <p className='text-center text-secondary'>{errorMsg.description}</p>
      <Button size='sm' variant='dark' onClick={onClose}>{t('common.close')}</Button>
    </div>
  )
}

ShelfWrapper.Skeleton = ShelfVisualizerSkeleton
ShelfWrapper.ErrorMsg = ErrorMsg
export default ShelfWrapper